import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../components/shared/DashboardLayout';
import { useTheme } from '../context/ThemeContext';
import API from '../utils/api';
import { isNative } from '../utils/capacitor';
import { copyToClipboard } from '../utils/clipboard';

const GENRE_ICONS = {
  magical:'✨', adventure:'🗺️', adventurous:'🗺️', funny:'😄',
  calm:'🌙', educational:'📚', cozy:'🔥', exciting:'⚡',
  spooky:'👻', fantasy:'🧙', mystery:'🔍', nature:'🌿',
  science:'🔬', bedtime:'🛌', default:'📖',
};
const capitalize = s => s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';

// ─── Animated counter ─────────────────────────────────────────────────────────
function useCounter(target, active, duration = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active || !target) { setVal(target || 0); return; }
    let frame;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(ease * target));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, active, duration]);
  return val;
}

// ─── Scroll-triggered reveal ──────────────────────────────────────────────────
function useReveal(rootMargin = '-60px') {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [rootMargin]);
  return [ref, visible];
}

// ─── Canvas helpers ───────────────────────────────────────────────────────────
function drawCardStars(ctx, W, H, seed) {
  for (let i = 0; i < 130; i++) {
    const x = ((seed * 7 + i * 137.508) % W + W) % W;
    const y = ((seed * 13 + i * 97.31) % H + H) % H;
    ctx.beginPath();
    ctx.arc(x, y, 0.5 + (i % 5) * 0.3, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255,255,255,${0.1 + (i % 6) * 0.07})`;
    ctx.fill();
  }
  // Bright twinkle stars
  for (let i = 0; i < 22; i++) {
    const x = ((seed * 3 + i * 211.7) % W + W) % W;
    const y = ((seed * 17 + i * 163.1) % H + H) % H;
    const sz = 2 + (i % 3);
    ctx.save();
    ctx.globalAlpha = 0.3 + (i % 4) * 0.1;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x - 1, y - sz, 2, sz * 2);
    ctx.fillRect(x - sz, y - 1, sz * 2, 2);
    ctx.beginPath(); ctx.arc(x, y, 1.5, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }
}

function drawCardGlow(ctx, x, y, r, color) {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, color);
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
}

function rrPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y); ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// ─── Canvas card generator ────────────────────────────────────────────────────
function generateWrappedCanvas(stats, child, year) {
  const W = 1080, H = 1080;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Background
  const bg = ctx.createLinearGradient(0, 0, W * 0.6, H);
  bg.addColorStop(0, '#06021a');
  bg.addColorStop(0.4, '#160840');
  bg.addColorStop(0.72, '#2d1b69');
  bg.addColorStop(1, '#4a1080');
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

  drawCardStars(ctx, W, H, year + child.name.length);
  drawCardGlow(ctx, 200, 200, 320, 'rgba(111,66,193,0.5)');
  drawCardGlow(ctx, W - 180, H - 220, 310, 'rgba(72,12,126,0.45)');
  drawCardGlow(ctx, W / 2, 500, 260, 'rgba(249,199,79,0.05)');

  ctx.textAlign = 'center';

  // "Year in Stories" header label
  ctx.fillStyle = 'rgba(201,168,255,0.68)';
  ctx.font = 'bold 40px system-ui,sans-serif';
  ctx.fillText('\u2728  Year in Stories', W / 2, 84);

  // Year — big gold
  ctx.fillStyle = '#f9c74f';
  ctx.font = 'bold 152px system-ui,sans-serif';
  ctx.fillText(String(year), W / 2, 244);

  // Avatar circle
  ctx.fillStyle = 'rgba(255,255,255,0.1)';
  ctx.beginPath(); ctx.arc(W / 2, 320, 46, 0, Math.PI * 2); ctx.fill();
  ctx.font = '52px sans-serif';
  ctx.textBaseline = 'middle';
  ctx.fillText(child.avatar || '\uD83E\uDDD2', W / 2, 320);
  ctx.textBaseline = 'alphabetic';

  // Child name
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 62px system-ui,sans-serif';
  ctx.fillText(child.name, W / 2, 428);

  // Divider
  ctx.strokeStyle = 'rgba(201,168,255,0.2)'; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(110, 460); ctx.lineTo(W - 110, 460); ctx.stroke();

  // 2×2 stats grid
  const totalMins = stats.totalReadingMins || 0;
  const hours = Math.floor(totalMins / 60);
  const mins  = totalMins % 60;
  const timeDisplay = totalMins === 0 ? '—'
    : hours > 0 ? (mins > 0 ? `${hours}h ${mins}m` : `${hours}h`)
    : `${totalMins}m`;
  const genreKey  = (stats.favoriteGenre || '').toLowerCase();
  const genreEmoji = GENRE_ICONS[genreKey] || '\uD83D\uDCD6';
  const genreLabel = stats.favoriteGenre ? capitalize(stats.favoriteGenre) : 'Mixed';

  const cells = [
    { icon: '\uD83D\uDCDA', val: String(stats.totalStories || 0), label: 'stories created' },
    { icon: '\uD83C\uDF0D', val: String(stats.uniqueAdventures || 0), label: 'magical worlds' },
    { icon: '\u23F1\uFE0F', val: timeDisplay, label: 'of bedtime magic' },
    { icon: genreEmoji, val: genreLabel, label: 'favourite genre' },
  ];

  const pad = 40, gap = 18;
  const cellW = (W - pad * 2 - gap) / 2;
  const cellH = 202;
  const gridTop = 482;

  cells.forEach((cell, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x  = pad + col * (cellW + gap);
    const y  = gridTop + row * (cellH + gap);
    const cx = x + cellW / 2;

    ctx.fillStyle = 'rgba(255,255,255,0.07)';
    rrPath(ctx, x, y, cellW, cellH, 24);
    ctx.fill();

    ctx.textAlign = 'center';

    // Icon
    ctx.font = '46px sans-serif';
    ctx.fillText(cell.icon, cx, y + 54);

    // Value
    ctx.fillStyle = '#f9c74f';
    const longVal = cell.val.length > 5;
    ctx.font = `bold ${longVal ? 50 : 66}px system-ui,sans-serif`;
    ctx.fillText(cell.val, cx, y + (longVal ? 126 : 132));

    // Label
    ctx.fillStyle = 'rgba(201,168,255,0.78)';
    ctx.font = '25px system-ui,sans-serif';
    ctx.fillText(cell.label, cx, y + 174);
  });

  // Bottom branding bar
  ctx.fillStyle = 'rgba(0,0,0,0.42)';
  ctx.fillRect(0, H - 78, W, 78);
  ctx.fillStyle = 'rgba(201,168,255,0.58)';
  ctx.font = '30px system-ui,sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('\uD83C\uDF19  dreamybedtime.com', W / 2, H - 26);

  return canvas;
}

// ─── Animated stat section ────────────────────────────────────────────────────
function StatSection({ gradient, icon, value, isNumeric, label, sublabel, delay = 0, children }) {
  const [ref, visible] = useReveal();
  const count = useCounter(isNumeric ? (value || 0) : 0, visible && isNumeric);

  return (
    <div
      ref={ref}
      className="rounded-4 p-4 p-md-5 mb-4 text-center"
      style={{
        background: gradient,
        minHeight: '200px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(36px)',
        transition: `opacity 0.65s ease ${delay}s, transform 0.65s ease ${delay}s`,
      }}
    >
      <div style={{ fontSize: 'clamp(38px,7vw,58px)', marginBottom: '10px', lineHeight: 1 }}>{icon}</div>
      <div className="fw-bold text-white"
        style={{ fontSize: 'clamp(48px,11vw,88px)', lineHeight: 1, fontFamily: "'Fredoka One',cursive" }}>
        {isNumeric ? count.toLocaleString() : value}
      </div>
      <div className="fw-semibold mt-2"
        style={{ color: 'rgba(255,255,255,0.82)', fontSize: 'clamp(15px,3vw,20px)' }}>
        {label}
      </div>
      {sublabel && (
        <div className="mt-1"
          style={{ color: 'rgba(255,255,255,0.52)', fontSize: 'clamp(12px,2.2vw,15px)' }}>
          {sublabel}
        </div>
      )}
      {children}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
const YearInStoriesPage = () => {
  const { id } = useParams();
  const { darkMode: dm } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [cardGenerating, setCardGenerating] = useState(false);
  const [cardUrl, setCardUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const currentYear = new Date().getFullYear();
  const availableYears = [];
  for (let y = currentYear; y >= 2024; y--) availableYears.push(y);

  useEffect(() => {
    setLoading(true);
    setData(null);
    setCardUrl(null);
    API.get(`/children/${id}/wrapped?year=${year}`)
      .then(res => setData(res.data))
      .catch(() => setError('Could not load your Year in Stories.'))
      .finally(() => setLoading(false));
  }, [id, year]);

  const generateCard = useCallback(async () => {
    if (!data || cardGenerating) return;
    setCardGenerating(true);
    try {
      const canvas = generateWrappedCanvas(data.stats, data.child, data.year);
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      setCardUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
    } catch (e) {
      console.error('Wrapped card generation failed', e);
    } finally {
      setCardGenerating(false);
    }
  }, [data, cardGenerating]);

  const handleDownload = () => {
    if (!cardUrl || !data) return;
    const a = document.createElement('a');
    a.href = cardUrl;
    a.download = `${data.child.name}-year-in-stories-${data.year}.png`;
    a.click();
  };

  const buildShareText = (d) => {
    const { child, stats, year: y } = d;
    const hours = Math.floor(stats.totalReadingMins / 60);
    const timeStr = hours >= 1 ? `${hours}h` : `${stats.totalReadingMins}m`;
    const genre = capitalize(stats.favoriteGenre) || 'Adventure';
    return `${child.avatar || '🌙'} ${child.name}'s ${y} Year in Stories!\n\n📚 ${stats.totalStories} stories created\n🌍 ${stats.uniqueAdventures} magical worlds explored\n⏱️ ${timeStr} of bedtime magic\n✨ Favourite genre: ${genre}${stats.topLearningTopic ? `\n🎓 Learned about: "${stats.topLearningTopic}"` : ''}\n\n🌙 dreamybedtime.com`;
  };

  const handleCopy = async () => {
    if (!data) return;
    const ok = await copyToClipboard(buildShareText(data));
    if (!ok) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleNativeShare = async () => {
    if (!data) return;
    const text = buildShareText(data);
    if (!navigator.share) { handleCopy(); return; }
    try {
      if (cardUrl && navigator.canShare) {
        const blob = await fetch(cardUrl).then(r => r.blob());
        const file = new File([blob], `year-in-stories-${data.year}.png`, { type: 'image/png' });
        const payload = { title: `${data.child.name}'s Year in Stories`, text, files: [file] };
        if (navigator.canShare(payload)) { await navigator.share(payload); return; }
      }
      await navigator.share({ title: `${data.child.name}'s Year in Stories`, text });
    } catch { /* user cancelled */ }
  };

  if (loading) return (
    <DashboardLayout title="Year in Stories">
      <div className="text-center py-5">
        <div className="spinner-border" style={{ color: '#6f42c1', width: '3rem', height: '3rem' }}></div>
        <p className="text-muted mt-3">Loading your year in stories…</p>
      </div>
    </DashboardLayout>
  );

  if (error) return (
    <DashboardLayout title="Year in Stories">
      <div className="alert alert-danger">{error}</div>
      <Link to="/dashboard/children" className="btn btn-outline-secondary rounded-pill">← Back to Profiles</Link>
    </DashboardLayout>
  );

  const { child, stats } = data;
  const {
    totalStories, totalReads, totalReadingMins, totalFaves,
    favoriteGenre, topLearningTopic, topicCount,
    mostReadStory, monthlyBreakdown, peakMonth,
    uniqueAdventures, avgRating, ratedCount,
  } = stats;

  const hours = Math.floor(totalReadingMins / 60);
  const mins  = totalReadingMins % 60;
  const timeDisplay = totalReadingMins === 0 ? null
    : hours > 0 ? (mins > 0 ? `${hours}h ${mins}m` : `${hours}h`) : `${totalReadingMins}m`;
  const maxMonthCount = Math.max(...(monthlyBreakdown || []).map(m => m.count), 1);
  const genreKey  = (favoriteGenre || '').toLowerCase();
  const genreIcon = GENRE_ICONS[genreKey] || '📖';
  const hasData   = totalStories > 0;

  return (
    <DashboardLayout title="Year in Stories">
      <style>{`
        @keyframes wrappedFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes wrappedShine { 0%{opacity:0.6} 50%{opacity:1} 100%{opacity:0.6} }
        @media print {
          .no-print { display: none !important; }
        }
      `}</style>

      <div style={{ maxWidth: '700px', margin: '0 auto' }}>

        {/* Back nav */}
        <div className="mb-3">
          <Link to={`/dashboard/children/${id}/report`}
            className="btn btn-sm btn-outline-secondary rounded-pill">
            <i className="bi bi-arrow-left me-1"></i>Reading Report
          </Link>
        </div>

        {/* ── Hero banner ── */}
        <div className="rounded-4 p-4 p-md-5 mb-4 text-center text-white position-relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg,#06021a 0%,#160840 40%,#2d1b69 72%,#4a1080 100%)', minHeight: '260px' }}>
          {/* Decorative circles */}
          <div style={{ position:'absolute', top:'-30px', right:'-30px', width:'200px', height:'200px', borderRadius:'50%', background:'rgba(201,168,255,0.06)', pointerEvents:'none' }} />
          <div style={{ position:'absolute', bottom:'-50px', left:'-25px', width:'160px', height:'160px', borderRadius:'50%', background:'rgba(249,199,79,0.05)', pointerEvents:'none' }} />

          <div style={{ position: 'relative' }}>
            <div style={{ fontSize: '56px', marginBottom: '8px', display: 'inline-block', animation: 'wrappedFloat 3s ease-in-out infinite' }}>🌙</div>
            <h2 className="fw-bold mb-1" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(20px,5vw,32px)' }}>
              {child.avatar} {child.name}'s Year in Stories
            </h2>
            <p style={{ color: 'rgba(201,168,255,0.72)', fontSize: '14px', marginBottom: '20px' }}>
              A magical recap of your bedtime adventures ✨
            </p>

            {/* Year selector pills */}
            {availableYears.length > 1 && (
              <div className="d-flex justify-content-center gap-2 flex-wrap">
                {availableYears.map(y => (
                  <button key={y} onClick={() => setYear(y)}
                    className="btn btn-sm rounded-pill fw-bold"
                    style={{
                      background: year === y ? '#f9c74f' : 'rgba(255,255,255,0.1)',
                      border: year === y ? 'none' : '1px solid rgba(255,255,255,0.25)',
                      color: year === y ? '#1a0a4a' : 'rgba(255,255,255,0.8)',
                      padding: '5px 22px',
                      transition: 'all 0.15s',
                    }}>
                    {y}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {!hasData ? (
          /* ── Empty state ── */
          <div className="card border-0 shadow-sm p-5 text-center" style={{ borderRadius: '20px' }}>
            <div style={{ fontSize: '56px', marginBottom: '16px' }}>📭</div>
            <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>No stories in {year} yet</h5>
            <p className="text-muted mb-4">
              Start creating magical bedtime stories for {child.name} and come back for a full year recap!
            </p>
            <Link to="/dashboard/generate"
              className="btn rounded-pill px-4 mx-auto fw-semibold"
              style={{ background: 'linear-gradient(135deg,#6f42c1,#4a1080)', color: 'white', width: 'fit-content' }}>
              <i className="bi bi-magic me-2"></i>Generate First Story
            </Link>
          </div>
        ) : (
          <>
            {/* ── Stories count ── */}
            <StatSection
              gradient="linear-gradient(135deg,#2d1b69,#4a1080)"
              icon="📚"
              value={totalStories}
              isNumeric={true}
              label={`${totalStories === 1 ? 'story' : 'stories'} created in ${year}`}
              sublabel="One adventure at a time 🌟"
            />

            {/* ── Reading time ── */}
            {timeDisplay && (
              <StatSection
                gradient="linear-gradient(135deg,#0c2a56,#0d4f85)"
                icon="⏱️"
                value={timeDisplay}
                isNumeric={false}
                label="of bedtime magic"
                sublabel={totalReads > 0 ? `across ${totalReads.toLocaleString()} total reads` : undefined}
                delay={0.05}
              />
            )}

            {/* ── Magical worlds ── */}
            {uniqueAdventures > 0 && (
              <StatSection
                gradient="linear-gradient(135deg,#064e3b,#065f46,#047857)"
                icon="🌍"
                value={uniqueAdventures}
                isNumeric={true}
                label="magical worlds explored"
                sublabel="Every story is a new universe"
                delay={0.05}
              />
            )}

            {/* ── Favourite genre ── */}
            {favoriteGenre && (
              <StatSection
                gradient="linear-gradient(135deg,#78350f,#92400e,#b45309)"
                icon={genreIcon}
                value={capitalize(favoriteGenre)}
                isNumeric={false}
                label="was the favourite genre"
                sublabel={`${child.name} knows what they love ✨`}
                delay={0.05}
              />
            )}

            {/* ── Most loved story ── */}
            {mostReadStory && (
              <MostLovedCard story={mostReadStory} />
            )}

            {/* ── Learning topics ── */}
            {topicCount > 0 && (
              <StatSection
                gradient="linear-gradient(135deg,#134e4a,#0f766e,#0d9488)"
                icon="🎓"
                value={topicCount}
                isNumeric={true}
                label={`${topicCount === 1 ? 'topic' : 'topics'} discovered through stories`}
                sublabel={topLearningTopic ? `Most explored: "${topLearningTopic}"` : 'Learning woven into every adventure'}
                delay={0.05}
              />
            )}

            {/* ── Peak month + chart ── */}
            {peakMonth && (
              <MonthSection monthlyBreakdown={monthlyBreakdown} peakMonth={peakMonth} maxMonthCount={maxMonthCount} />
            )}

            {/* ── Avg rating ── */}
            {avgRating && ratedCount > 0 && (
              <StatSection
                gradient="linear-gradient(135deg,#713f12,#92400e,#d97706)"
                icon="⭐"
                value={`${avgRating}/5`}
                isNumeric={false}
                label="average story rating"
                sublabel={`Across ${ratedCount} rated ${ratedCount === 1 ? 'story' : 'stories'}`}
                delay={0.05}
              />
            )}

            {/* ── Favourites ── */}
            {totalFaves > 0 && (
              <StatSection
                gradient="linear-gradient(135deg,#4a0505,#7f1d1d,#b91c1c)"
                icon="❤️"
                value={totalFaves}
                isNumeric={true}
                label={`${totalFaves === 1 ? 'story' : 'stories'} saved to favourites`}
                sublabel="The ones worth coming back to"
                delay={0.05}
              />
            )}

            {/* ── Luna letter ── */}
            <LunaLetterSection child={child} stats={stats} year={year} dm={dm} />

            {/* ── Share card ── */}
            <ShareSection
              data={data}
              cardUrl={cardUrl}
              cardGenerating={cardGenerating}
              copied={copied}
              dm={dm}
              onGenerate={generateCard}
              onDownload={handleDownload}
              onCopy={handleCopy}
              onNativeShare={handleNativeShare}
              shareText={buildShareText(data)}
            />

            {/* ── At-a-glance recap ── */}
            <AtAGlanceCard stats={stats} year={year} dm={dm} genreIcon={genreIcon} timeDisplay={timeDisplay} />
          </>
        )}

        {/* Footer nav */}
        <div className="text-center pb-5 d-flex justify-content-center gap-2 flex-wrap no-print">
          <Link to={`/dashboard/children/${id}/report`}
            className="btn btn-sm btn-outline-secondary rounded-pill px-4">
            <i className="bi bi-bar-chart-fill me-1"></i>Full Reading Report
          </Link>
          <Link to="/dashboard/children"
            className="btn btn-sm btn-outline-secondary rounded-pill px-4">
            <i className="bi bi-people me-1"></i>All Children
          </Link>
          {/* Hidden in the apps: window.print() is a no-op in the Android/iOS
              WebView, so this button did nothing at all when tapped. */}
          {!isNative() && (
            <button className="btn btn-sm btn-outline-secondary rounded-pill px-4"
              onClick={() => window.print()}>
              <i className="bi bi-printer me-1"></i>Print / Save PDF
            </button>
          )}
        </div>

      </div>
    </DashboardLayout>
  );
};

// ─── Sub-components (keep hooks legal — no conditional hook calls) ─────────────

function MostLovedCard({ story }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} className="rounded-4 p-4 p-md-5 mb-4"
      style={{
        background: 'linear-gradient(135deg,#4a0e2c,#7d1d5a)',
        color: 'white',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(36px)',
        transition: 'opacity 0.65s ease, transform 0.65s ease',
      }}>
      <div className="text-center mb-3">
        <div style={{ fontSize: '42px', marginBottom: '8px' }}>🏆</div>
        <p className="fw-semibold mb-0" style={{ color: 'rgba(255,255,255,0.65)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '2px' }}>
          Most Loved Story
        </p>
      </div>
      <Link to={`/dashboard/story/${story._id}`} className="text-decoration-none">
        <div className="rounded-3 overflow-hidden"
          style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)' }}>
          {story.illustrationUrl && (
            <img src={story.illustrationUrl} alt={story.title}
              style={{ width: '100%', height: '150px', objectFit: 'cover' }} />
          )}
          <div className="p-3">
            <div className="fw-bold text-white mb-2" style={{ fontSize: '16px', lineHeight: '1.3' }}>
              {story.title}
            </div>
            <div className="d-flex align-items-center gap-2">
              <i className="bi bi-eye-fill" style={{ color: '#f9c74f', fontSize: '13px' }}></i>
              <span style={{ color: 'rgba(255,255,255,0.72)', fontSize: '13px' }}>
                {story.readCount} {story.readCount === 1 ? 'read' : 'reads'}
              </span>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}

function MonthSection({ monthlyBreakdown, peakMonth, maxMonthCount }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} className="rounded-4 p-4 p-md-5 mb-4"
      style={{
        background: 'linear-gradient(135deg,#1e1b4b,#312e81,#4338ca)',
        color: 'white',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(36px)',
        transition: 'opacity 0.65s ease, transform 0.65s ease',
      }}>
      <div className="text-center mb-4">
        <div style={{ fontSize: '42px', marginBottom: '8px' }}>📅</div>
        <div className="fw-bold"
          style={{ fontSize: 'clamp(30px,7vw,52px)', fontFamily: "'Fredoka One',cursive" }}>
          {peakMonth}
        </div>
        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '15px', marginTop: '4px' }}>
          was your most magical month
        </div>
      </div>
      {/* Mini bar chart */}
      <div className="d-flex align-items-end justify-content-center gap-1" style={{ height: '80px' }}>
        {monthlyBreakdown.map((m) => {
          const isPeak = m.count === maxMonthCount && m.count > 0;
          return (
            <div key={m.month} className="d-flex flex-column align-items-center" style={{ flex: 1, maxWidth: '44px' }}>
              <div style={{
                width: '100%', minHeight: '4px',
                height: `${Math.max(m.count > 0 ? 8 : 0, (m.count / maxMonthCount) * 62)}px`,
                background: isPeak ? '#f9c74f' : 'rgba(255,255,255,0.28)',
                borderRadius: '4px 4px 0 0',
                transition: visible ? 'height 0.6s ease' : 'none',
              }} />
              <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.45)', marginTop: '3px' }}>{m.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function buildLunaLetter(child, stats, year) {
  const { totalStories, totalReadingMins, favoriteGenre, topLearningTopic, totalFaves, uniqueAdventures } = stats;
  const hours = Math.floor((totalReadingMins || 0) / 60);
  const timePhrase = hours >= 2 ? `over ${hours} hours` : totalReadingMins >= 30 ? `${totalReadingMins} minutes` : null;
  const genrePhrase = favoriteGenre ? `${favoriteGenre} stories` : 'magical stories';
  const worldsPhrase = uniqueAdventures > 3 ? `${uniqueAdventures} different worlds` : uniqueAdventures > 0 ? `${uniqueAdventures} unique worlds` : null;
  const favPhrase = totalFaves >= 3 ? `${totalFaves} favourites` : null;
  const topicPhrase = topLearningTopic ? ` Along the way, ${child.name} even discovered "${topLearningTopic}" — that curiosity is something I'll treasure.` : '';

  let opening = totalStories >= 50
    ? `What a year this has been! ${totalStories} stories together — you didn't just read bedtime stories, you built a whole universe.`
    : totalStories >= 20
    ? `${totalStories} bedtime stories in ${year}! Every single night that you opened Dreamy Bedtime was a gift to ${child.name}.`
    : totalStories >= 10
    ? `What a wonderful year! ${totalStories} stories is no small thing — that's ${totalStories} nights of connection.`
    : `${totalStories} stories in ${year}. Each one was a tiny act of love — choosing to be present at bedtime.`;

  let middle = `${child.name} has such a wonderful spirit for ${genrePhrase}.${topicPhrase}`;
  if (worldsPhrase) middle += ` Together, you explored ${worldsPhrase} — the kind of imaginative richness that stays with a child long after the lights go out.`;
  if (timePhrase) middle += ` That's ${timePhrase} of pure togetherness — ${timePhrase} where everything else faded away and it was just the two of you.`;
  if (favPhrase) middle += ` And ${child.name} saved ${favPhrase} — the ones they'll want to hear again and again.`;

  const closing = `I'll be here every night, ready for the next adventure. Thank you for making Dreamy Bedtime part of your family's story. Here's to an even more magical ${year + 1}.\n\nWith starlight and love,\n🌙 Luna`;

  return `Dear parent of ${child.name},\n\n${opening}\n\n${middle}\n\n${closing}`;
}

function LunaLetterSection({ child, stats, year, dm }) {
  const [ref, visible] = useReveal();
  const letter = buildLunaLetter(child, stats, year);

  return (
    <div ref={ref} className="mb-4"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(36px)',
        transition: 'opacity 0.65s ease, transform 0.65s ease',
      }}>
      <div className="rounded-4 p-4 p-md-5 position-relative overflow-hidden"
        style={{
          background: dm
            ? 'linear-gradient(135deg,rgba(45,27,105,0.5) 0%,rgba(26,20,56,0.9) 100%)'
            : 'linear-gradient(135deg,#f0e8ff 0%,#e8d5ff 100%)',
          border: dm ? '1px solid rgba(111,66,193,0.3)' : '1px solid #d4b8ff',
        }}>
        {/* Decorative moon glow */}
        <div style={{
          position: 'absolute', top: '-20px', right: '-20px',
          width: '130px', height: '130px', borderRadius: '50%',
          background: 'radial-gradient(circle,rgba(249,199,79,0.18) 0%,transparent 70%)',
          pointerEvents: 'none',
        }} />

        {/* Header */}
        <div className="d-flex align-items-center gap-3 mb-4">
          <div style={{
            width: '52px', height: '52px', borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(135deg,#2d1b69,#6f42c1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '26px', boxShadow: '0 0 18px rgba(111,66,193,0.45)',
          }}>🌙</div>
          <div>
            <div className="fw-bold" style={{ color: 'var(--text-main)', fontSize: '15px' }}>A letter from Luna</div>
            <div className="text-muted" style={{ fontSize: '12px' }}>Your {year} Year in Stories — from me to you</div>
          </div>
        </div>

        {/* Letter body */}
        <div className="rounded-3 p-4"
          style={{
            background: dm ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.7)',
            border: dm ? '1px solid rgba(201,168,255,0.15)' : '1px solid rgba(201,168,255,0.4)',
          }}>
          <pre style={{
            whiteSpace: 'pre-wrap', fontFamily: 'Georgia, serif',
            fontSize: 'clamp(13px,2.2vw,15px)', lineHeight: '1.85',
            color: 'var(--text-body)', margin: 0,
          }}>
            {letter}
          </pre>
        </div>

        {/* Signature flourish */}
        <div className="text-center mt-4">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <div style={{ flex: 1, height: '1px', background: dm ? 'rgba(201,168,255,0.2)' : 'rgba(111,66,193,0.2)' }} />
            <span style={{ fontSize: '20px' }}>✨</span>
            <div style={{ flex: 1, height: '1px', background: dm ? 'rgba(201,168,255,0.2)' : 'rgba(111,66,193,0.2)' }} />
          </div>
          <p className="mt-3 mb-0 small" style={{ color: dm ? 'rgba(201,168,255,0.55)' : 'rgba(111,66,193,0.6)', fontStyle: 'italic' }}>
            Generated by Dreamy Bedtime · dreamybedtime.com
          </p>
        </div>
      </div>
    </div>
  );
}

function ShareSection({ data, cardUrl, cardGenerating, copied, dm, onGenerate, onDownload, onCopy, onNativeShare, shareText }) {
  const [ref, visible] = useReveal();
  const { child, year } = data;
  return (
    <div ref={ref} className="card border-0 shadow-sm mb-4"
      style={{
        borderRadius: '20px', overflow: 'hidden',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(36px)',
        transition: 'opacity 0.65s ease, transform 0.65s ease',
      }}>
      <div style={{ height: '5px', background: 'linear-gradient(90deg,#2d1b69,#f9c74f,#2d1b69)' }} />
      <div className="card-body p-4 p-md-5">
        <div className="text-center mb-4">
          <div style={{ fontSize: '36px', marginBottom: '8px' }}>🎁</div>
          <h5 className="fw-bold mb-1" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive" }}>
            Share {child.name}'s Wrapped
          </h5>
          <p className="text-muted small mb-0">Download a card or copy the summary to share with friends and family</p>
        </div>

        {/* Card preview */}
        <div className="text-center mb-4">
          {!cardUrl ? (
            <>
              {/* Placeholder */}
              <div className="rounded-3 mx-auto d-flex align-items-center justify-content-center mb-3"
                style={{
                  width: '100%', maxWidth: '300px', aspectRatio: '1/1',
                  background: 'linear-gradient(135deg,#06021a,#2d1b69)',
                  cursor: cardGenerating ? 'default' : 'pointer',
                }}
                onClick={!cardGenerating ? onGenerate : undefined}>
                {cardGenerating
                  ? <div className="spinner-border" style={{ color: '#c9a8ff', width: '3rem', height: '3rem' }} />
                  : (
                    <div className="text-center p-4">
                      <div style={{ fontSize: '52px', marginBottom: '12px', animation: 'wrappedShine 2s ease-in-out infinite' }}>🌙</div>
                      <div className="fw-bold" style={{ color: '#c9a8ff', fontSize: '15px' }}>Tap to generate your card</div>
                    </div>
                  )}
              </div>
              <button className="btn rounded-pill px-4 fw-semibold"
                style={{ background: 'linear-gradient(135deg,#6f42c1,#4a1080)', color: 'white' }}
                onClick={onGenerate} disabled={cardGenerating}>
                {cardGenerating
                  ? <><span className="spinner-border spinner-border-sm me-2" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />Generating…</>
                  : <><i className="bi bi-image me-2"></i>Generate Share Card</>}
              </button>
            </>
          ) : (
            <>
              <img src={cardUrl} alt={`${child.name}'s ${year} Year in Stories`}
                className="rounded-3 img-fluid d-block mx-auto mb-3 shadow"
                style={{ maxWidth: '300px', width: '100%' }} />
              <div className="d-flex flex-wrap justify-content-center gap-2">
                <button className="btn rounded-pill px-4 fw-semibold"
                  style={{ background: 'linear-gradient(135deg,#6f42c1,#4a1080)', color: 'white' }}
                  onClick={onDownload}>
                  <i className="bi bi-download me-2"></i>Download PNG
                </button>
                {navigator.share && (
                  <button className="btn btn-outline-secondary rounded-pill px-4 fw-semibold"
                    onClick={onNativeShare}>
                    <i className="bi bi-share me-2"></i>Share
                  </button>
                )}
                <button className="btn rounded-pill px-4 fw-semibold"
                  style={{ background: 'rgba(111,66,193,0.1)', color: '#6f42c1', border: '1px solid rgba(111,66,193,0.3)' }}
                  onClick={onGenerate}>
                  <i className="bi bi-arrow-clockwise me-1"></i>Regenerate
                </button>
              </div>
            </>
          )}
        </div>

        {/* Share text */}
        <div className="rounded-3 p-3 mb-3"
          style={{ background: dm ? 'rgba(111,66,193,0.15)' : '#f0e8ff', border: '1px solid #e8d5ff' }}>
          <div className="small fw-semibold mb-2" style={{ color: '#6f42c1' }}>Share text</div>
          <pre className="mb-0 small"
            style={{ whiteSpace: 'pre-wrap', color: 'var(--text-main)', fontFamily: 'inherit', lineHeight: '1.6' }}>
            {shareText}
          </pre>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button className="btn btn-sm rounded-pill px-3 fw-semibold"
            style={{ background: 'linear-gradient(135deg,#6f42c1,#4a1080)', color: 'white' }}
            onClick={onCopy}>
            <i className={`bi ${copied ? 'bi-check-lg' : 'bi-clipboard'} me-1`}></i>
            {copied ? 'Copied!' : 'Copy text'}
          </button>
          {navigator.share && !cardUrl && (
            <button className="btn btn-sm btn-outline-secondary rounded-pill px-3 fw-semibold"
              onClick={onNativeShare}>
              <i className="bi bi-share me-1"></i>Share
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AtAGlanceCard({ stats, year, dm, genreIcon, timeDisplay }) {
  const [ref, visible] = useReveal();
  const {
    totalStories, totalReads, uniqueAdventures, totalFaves,
    favoriteGenre, topLearningTopic, avgRating, peakMonth,
  } = stats;

  const items = [
    { icon: '📚', val: totalStories.toLocaleString(), label: 'Stories' },
    { icon: '👁️', val: totalReads.toLocaleString(), label: 'Total Reads' },
    { icon: '🌍', val: uniqueAdventures, label: 'Worlds' },
    { icon: '❤️', val: totalFaves, label: 'Favourites' },
    ...(timeDisplay ? [{ icon: '⏱️', val: timeDisplay, label: 'Reading Time' }] : []),
    ...(favoriteGenre ? [{ icon: genreIcon, val: capitalize(favoriteGenre), label: 'Top Genre' }] : []),
    ...(topLearningTopic ? [{ icon: '🎓', val: `"${topLearningTopic}"`, label: 'Top Topic' }] : []),
    ...(avgRating ? [{ icon: '⭐', val: `${avgRating}/5`, label: 'Avg Rating' }] : []),
    ...(peakMonth ? [{ icon: '📅', val: peakMonth, label: 'Peak Month' }] : []),
  ];

  return (
    <div ref={ref} className="card border-0 shadow-sm mb-4"
      style={{
        borderRadius: '20px', overflow: 'hidden',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(36px)',
        transition: 'opacity 0.65s ease, transform 0.65s ease',
      }}>
      <div style={{ height: '4px', background: 'linear-gradient(90deg,#2d1b69,#6f42c1)' }} />
      <div className="card-body p-4">
        <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>
          <i className="bi bi-stars me-2" style={{ color: '#f9c74f' }}></i>
          {year} at a Glance
        </h6>
        <div className="row g-2">
          {items.map((item, i) => (
            <div key={i} className="col-6 col-sm-4">
              <div className="d-flex align-items-center gap-2 p-2 rounded-3"
                style={{ background: dm ? 'rgba(111,66,193,0.12)' : '#f8f5ff' }}>
                <span style={{ fontSize: '18px', flexShrink: 0 }}>{item.icon}</span>
                <div style={{ minWidth: 0 }}>
                  <div className="fw-bold text-truncate" style={{ color: 'var(--text-main)', fontSize: '14px', lineHeight: 1.2 }}>
                    {item.val}
                  </div>
                  <div className="text-muted" style={{ fontSize: '11px' }}>{item.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default YearInStoriesPage;
